import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, finalize, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';

import { CvService } from '../cv.service';
import { ClientCvTemplateService } from '../templates/client-cv-template.service';
import { ClientCvTemplate } from '../../interfaces/client-cv-template.interface';
import {
  CvCurationOption,
  CvCurationPayload,
  CvDefaults,
  CvTemplate
} from '../../interfaces/cv';

/**
 * Slide-in side panel (or large modal host) that drives a full CV generation:
 * configure template + locale + client lens, toggle which experiences, skills,
 * languages and education items to include, see a live HTML preview in an
 * iframe, then download the final PDF.
 *
 * The parent is responsible for presenting the component inside an
 * nb-dialog, overlay or similar container. This component owns only its own
 * layout and lifecycle.
 */
@Component({
  selector: 'bst-cv-generate-panel',
  templateUrl: './cv-generate-panel.component.html',
  styleUrls: ['./cv-generate-panel.component.scss'],
  standalone: false
})
export class CvGeneratePanelComponent implements OnInit, OnDestroy {

  @Input() public userId!: number;
  @Input() public clientId?: number;
  /**
   * Optional vacancy id to tailor the CV to. In Phase 1 this is passed through
   * preview + generate payloads but has no rewriting effect; Phase 2 will use
   * it to drive AI-based tailoring in the transformer stage.
   */
  @Input() public vacancyId?: number | null;

  /** Emitted when the user dismisses the panel via the header close button. */
  @Output() public closed = new EventEmitter<void>();

  /** Emitted after a successful generation with the new record id. */
  @Output() public generated = new EventEmitter<{ id: number; download_url: string }>();

  public defaults: CvDefaults | null = null;
  public form: FormGroup | null = null;

  /** Static curation flow vs AI tailoring flow. Defaults to static. */
  public mode: 'static' | 'tailor' = 'static';

  /** Client ids entitled to the Tailor mode, sourced from defaults. */
  public tailoringClientIds: number[] = [];

  public previewHtml: SafeHtml | null = null;
  public previewLoading = false;
  public initialLoading = false;
  public downloading = false;
  public availableLocales: string[] = [];

  /**
   * Ready & active uploaded branded templates for every client the user has
   * access to, keyed by client_id. Lets the picker render one option group
   * per client without re-fetching when the user switches selection.
   */
  public brandedByClient = new Map<number, ClientCvTemplate[]>();

  /**
   * Friendly display names for locale codes shown in the language dropdown.
   * Falls back to the uppercase code for any locale we have not listed.
   */
  public readonly localeLabels: Record<string, string> = {
    en: 'English',
    nl: 'Nederlands',
  };

  private previewTrigger = new Subject<CvCurationPayload>();
  private subs: Subscription[] = [];

  constructor(
    private readonly fb: FormBuilder,
    private readonly cvService: CvService,
    private readonly clientCvTemplateService: ClientCvTemplateService,
    private readonly toastr: NbToastrService,
    private readonly translate: TranslateService,
    private readonly sanitizer: DomSanitizer,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.wirePreviewPipeline();
    this.loadDefaults();
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
    this.subs = [];
    this.previewTrigger.complete();
  }

  // --------------------------------------------------------------------
  // Initial load
  // --------------------------------------------------------------------

  private loadDefaults(): void {
    this.initialLoading = true;
    const params: { client_id?: number } = {};
    if (this.clientId !== undefined && this.clientId !== null) {
      params.client_id = this.clientId;
    }

    const sub = this.cvService.getDefaults(this.userId, params).subscribe({
      next: defaults => {
        this.defaults = defaults;
        this.tailoringClientIds = defaults.tailoring_client_ids ?? [];
        this.buildForm(defaults);
        this.initialLoading = false;
        this.triggerPreview();
        this.loadAllBrandedTemplates(defaults.clients.map(c => c.id));
      },
      error: (err: unknown) => {
        this.initialLoading = false;
        console.error('Failed to load CV defaults', err);
        this.toastr.danger(
          this.translate.instant('cv.errors.loadDefaultsFailed')
          || 'Could not load CV generator defaults.',
          this.translate.instant('cv.errors.title') || 'Error'
        );
      }
    });
    this.subs.push(sub);
  }

  private buildForm(defaults: CvDefaults): void {
    const cur = defaults.curation_defaults || ({} as CvCurationPayload);

    const initialTemplate = this.resolveInitialTemplate(defaults, cur.template_id);
    this.availableLocales = initialTemplate?.supported_locales?.length
      ? initialTemplate.supported_locales
      : defaults.locales || [];

    const initialLocale = cur.locale && this.availableLocales.includes(cur.locale)
      ? cur.locale
      : (this.availableLocales[0] || null);

    const initialClientId = cur.client_id
      ?? (this.clientId ?? (defaults.clients[0]?.id ?? null));

    // Default excludes 'address' on purpose even though it's a valid picker
    // value — most users don't want a home address on an outbound CV.
    const initialContactFields = (cur.contact_fields && cur.contact_fields.length > 0)
      ? cur.contact_fields
      : ['email', 'phone', 'linkedin_url'];

    this.form = this.fb.group({
      // template_id can resolve to a client's default branded base layout
      // (e.g. spotlight) via curation_defaults -- template_type/
      // client_cv_template_id must be seeded from the same source, or this
      // panel submits a layout built to sit under custom artwork with no
      // artwork selected to back it.
      template_id: new FormControl(initialTemplate?.id ?? null),
      template_type: new FormControl<'built_in' | 'client'>(cur.template_type ?? 'built_in'),
      client_cv_template_id: new FormControl<number | null>(cur.client_cv_template_id ?? null),
      locale: new FormControl(initialLocale),
      client_id: new FormControl(initialClientId),
      experience_ids: this.fb.array(
        (cur.experience_ids ?? []).map(id => new FormControl(id))
      ),
      education_ids: this.fb.array(
        (cur.education_ids ?? []).map(id => new FormControl(id))
      ),
      skill_ids: this.fb.array(
        (cur.skill_ids ?? []).map(id => new FormControl(id))
      ),
      language_ids: this.fb.array(
        (cur.language_ids ?? []).map(id => new FormControl(id))
      ),
      contact_fields: this.fb.array(
        initialContactFields.map(f => new FormControl(f))
      ),
      references_text: new FormControl(cur.references_text ?? null),
      // Default mirrors the client theme so the checkbox reflects what the
      // PDF would do before the user touches it. Until the user actually
      // toggles it, the payload sends null so the backend follows the
      // SELECTED client's theme (the panel only knows the initial client's
      // theme, so an explicit boolean here would stomp other clients'
      // settings). A carried-over explicit override survives form rebuilds.
      include_references: new FormControl<boolean>(
        cur.include_references ?? ((defaults.theme?.references_line ?? 'on') !== 'off')
      )
    });
    this.includeReferencesTouched = cur.include_references != null;

    // React to template changes: narrow the locale list.
    this.subs.push(
      this.form.get('template_id')!.valueChanges.subscribe((tid: string | null) => {
        const tpl = (this.defaults?.templates || []).find(t => t.id === tid);
        this.availableLocales = tpl?.supported_locales?.length
          ? tpl.supported_locales
          : (this.defaults?.locales || []);
        const localeCtrl = this.form!.get('locale');
        if (localeCtrl && !this.availableLocales.includes(localeCtrl.value)) {
          localeCtrl.setValue(this.availableLocales[0] || null);
        }
      })
    );

    // Debounced live preview on any form change.
    this.subs.push(
      this.form.valueChanges.pipe(debounceTime(300)).subscribe(() => this.triggerPreview())
    );
  }

  private resolveInitialTemplate(defaults: CvDefaults, preferredId?: string): CvTemplate | undefined {
    if (preferredId) {
      const match = defaults.templates.find(t => t.id === preferredId);
      if (match) return match;
    }
    return defaults.templates[0];
  }

  // --------------------------------------------------------------------
  // Preview pipeline
  // --------------------------------------------------------------------

  private wirePreviewPipeline(): void {
    // Sentinel emitted by catchError so the subscriber can distinguish a real
    // empty payload from a network/render error and decide whether to clear
    // the iframe or keep the prior HTML on screen.
    const PREVIEW_ERROR = Symbol('preview-error');

    const sub = this.previewTrigger.pipe(
      tap(() => { this.previewLoading = true; }),
      switchMap(payload => this.cvService.preview(this.userId, payload).pipe(
        catchError((err: unknown) => {
          console.error('Preview failed', err);
          this.toastr.warning(
            this.translate.instant('cv.errors.previewFailed')
            || 'Preview could not be rendered.',
            this.translate.instant('cv.errors.title') || 'Error'
          );
          return of<typeof PREVIEW_ERROR>(PREVIEW_ERROR);
        }),
        finalize(() => { this.previewLoading = false; })
      ))
    ).subscribe((html: string | typeof PREVIEW_ERROR) => {
      // Keep the previous preview on error so the user retains visual context
      // (otherwise switching to an unauthorized client wipes the iframe and
      // the dialog looks broken).
      if (html === PREVIEW_ERROR) return;
      // srcdoc is consumed by an <iframe>. Bypass sanitizer because it is
      // rendered inside an isolated iframe and the server produces trusted
      // HTML. Do NOT inject this into the main document DOM.
      this.previewHtml = this.sanitizer.bypassSecurityTrustHtml(html || '');
    });
    this.subs.push(sub);
  }

  public triggerPreview(): void {
    if (!this.form) return;
    const payload = this.buildPayload();
    if (!payload) return;
    this.previewTrigger.next(payload);
  }

  // --------------------------------------------------------------------
  // Payload helpers
  // --------------------------------------------------------------------

  private buildPayload(): CvCurationPayload | null {
    if (!this.form) return null;
    const v = this.form.value;
    if (!v.template_id || !v.locale || !v.client_id) return null;

    const payload: CvCurationPayload = {
      client_id: Number(v.client_id),
      template_id: v.template_id,
      locale: v.locale,
      vacancy_id: this.vacancyId ?? null,
      experience_ids: this.readIdArray('experience_ids'),
      education_ids: this.readIdArray('education_ids'),
      skill_ids: this.readIdArray('skill_ids'),
      language_ids: this.readIdArray('language_ids'),
      contact_fields: this.readStringArray('contact_fields'),
      references_text: v.references_text ?? null,
      include_references: this.includeReferencesTouched ? !!v.include_references : null
    };

    if (v.template_type === 'client' && v.client_cv_template_id) {
      payload.template_type = 'client';
      payload.client_cv_template_id = Number(v.client_cv_template_id);
    }

    return payload;
  }

  private readStringArray(controlName: string): string[] {
    const arr = this.form?.get(controlName) as FormArray | null;
    if (!arr) return [];
    return arr.controls
      .map(c => (c.value === null || c.value === undefined) ? '' : String(c.value))
      .filter(v => v !== '');
  }

  public isContactChecked(field: string): boolean {
    const arr = this.form?.get('contact_fields') as FormArray | null;
    if (!arr) return false;
    return arr.controls.some(c => c.value === field);
  }

  public toggleContact(field: string, checked: boolean): void {
    const arr = this.form?.get('contact_fields') as FormArray | null;
    if (!arr) return;
    const existingIdx = arr.controls.findIndex(c => c.value === field);
    if (checked && existingIdx === -1) {
      arr.push(new FormControl(field));
    } else if (!checked && existingIdx !== -1) {
      arr.removeAt(existingIdx);
    }
  }

  private includeReferencesTouched = false;

  public get includeReferences(): boolean {
    return !!this.form?.get('include_references')?.value;
  }

  public toggleReferences(checked: boolean): void {
    this.includeReferencesTouched = true;
    this.form?.get('include_references')?.setValue(checked);
  }

  private readIdArray(controlName: string): number[] {
    const arr = this.form?.get(controlName) as FormArray | null;
    if (!arr) return [];
    return arr.controls.map(c => Number(c.value)).filter(n => !Number.isNaN(n));
  }

  // --------------------------------------------------------------------
  // Uploaded client templates (PR6)
  // --------------------------------------------------------------------

  /**
   * Fetch ready+active branded templates for every client the user has access
   * to, in parallel, and bucket them by client_id. The picker renders one
   * option group per client without further round trips.
   */
  private loadAllBrandedTemplates(clientIds: number[]): void {
    const ids = clientIds.filter(id => id > 0);
    if (ids.length === 0) {
      this.brandedByClient = new Map();
      return;
    }
    const sub = forkJoin(
      ids.map(id => this.clientCvTemplateService.list(id).pipe(
        catchError(err => {
          console.error('cv-generate-panel: load branded templates for client failed', err);
          return of({ data: [] as ClientCvTemplate[] } as any);
        }),
      ))
    ).subscribe(pages => {
      const next = new Map<number, ClientCvTemplate[]>();
      pages.forEach((page, idx) => {
        const ready = (page?.data || []).filter(t => t.status === 'ready' && t.is_active);
        if (ready.length > 0) next.set(ids[idx], ready);
      });
      this.brandedByClient = next;
    });
    this.subs.push(sub);
  }

  /** Clients that actually have at least one usable branded template. */
  public get clientsWithBranded(): Array<{ id: number; name: string }> {
    return (this.defaults?.clients || [])
      .filter(c => (this.brandedByClient.get(c.id)?.length ?? 0) > 0)
      .map(c => ({ id: c.id, name: c.name || `Client #${c.id}` }));
  }

  public brandedFor(clientId: number): ClientCvTemplate[] {
    return this.brandedByClient.get(clientId) ?? [];
  }

  /** Flat lookup for picker selection. */
  private findBranded(id: number): ClientCvTemplate | null {
    for (const list of this.brandedByClient.values()) {
      const hit = list.find(t => t.id === id);
      if (hit) return hit;
    }
    return null;
  }

  /**
   * Encodes the current form state as a "<kind>:<id>" string for the unified
   * <nb-select>. Returns null while the form is still being built.
   */
  public get selectedPickerValue(): string | null {
    if (!this.form) return null;
    const v = this.form.value;
    if (v.template_type === 'client' && v.client_cv_template_id) {
      return `client:${v.client_cv_template_id}`;
    }
    return v.template_id ? `built_in:${v.template_id}` : null;
  }

  /**
   * Parses the picker value back to form state. Branded selections also
   * override template_id with the branded template's base_layout_id so the
   * renderer knows the page geometry, and switch client_id to whichever
   * client owns that branded template (a single picker change therefore
   * scopes the CV to the right client+theme atomically).
   */
  public onTemplatePickerChange(value: string | null): void {
    if (!this.form || !value) return;
    const [kind, rawId] = value.split(':');
    if (kind === 'client') {
      const id = Number(rawId);
      const tpl = this.findBranded(id);
      if (!tpl) return;
      this.form.patchValue({
        template_type: 'client',
        client_cv_template_id: id,
        template_id: tpl.base_layout_id,
        client_id: tpl.client_id,
      });
    } else {
      this.form.patchValue({
        template_type: 'built_in',
        client_cv_template_id: null,
        template_id: rawId,
      });
    }
  }

  // --------------------------------------------------------------------
  // Section toggles
  // --------------------------------------------------------------------

  public isChecked(controlName: string, id: number): boolean {
    const arr = this.form?.get(controlName) as FormArray | null;
    if (!arr) return false;
    return arr.controls.some(c => Number(c.value) === Number(id));
  }

  public toggle(controlName: string, id: number, checked: boolean): void {
    const arr = this.form?.get(controlName) as FormArray | null;
    if (!arr) return;
    const existingIdx = arr.controls.findIndex(c => Number(c.value) === Number(id));
    if (checked && existingIdx === -1) {
      arr.push(new FormControl(id));
    } else if (!checked && existingIdx !== -1) {
      arr.removeAt(existingIdx);
    }
  }

  // --------------------------------------------------------------------
  // Generate
  // --------------------------------------------------------------------

  public onGenerate(): void {
    if (this.downloading) return;
    const payload = this.buildPayload();
    if (!payload) {
      this.toastr.warning(
        this.translate.instant('cv.errors.incompleteForm')
        || 'Please choose a template, locale and client before generating.',
        this.translate.instant('cv.errors.title') || 'Error'
      );
      return;
    }

    this.downloading = true;
    const sub = this.cvService.generate(this.userId, payload).subscribe({
      next: result => {
        if (result?.id) {
          // fire-and-forget — keep `downloading` true until the blob has
          // actually been pulled so the button stays disabled.
          void this.triggerDownload(result.download_url || `/api/cv/${result.id}/download`);
        } else {
          this.downloading = false;
        }
        this.generated.emit(result);
      },
      error: (err: HttpErrorResponse) => {
        this.downloading = false;
        if (err?.status === 422 && err.error?.stale_ids) {
          this.handleStaleIds(err.error.stale_ids, payload);
        } else {
          console.error('CV generation failed', err);
          this.toastr.danger(
            this.translate.instant('cv.errors.generateFailed')
            || 'CV generation failed. Please try again.',
            this.translate.instant('cv.errors.title') || 'Error'
          );
        }
      }
    });
    this.subs.push(sub);
  }

  private async triggerDownload(url: string): Promise<void> {
    try {
      const resp = await fetch(url, { credentials: 'include' });
      if (!resp.ok) {
        console.error('cv-generate-panel: download HTTP error', { url, status: resp.status });
        this.toastr.danger(`HTTP ${resp.status}`, this.translate.instant('cv.errors.title') || 'Download failed');
        return;
      }
      const blob = await resp.blob();
      if (blob.size < 100) {
        console.error('cv-generate-panel: download empty/short blob', { url, size: blob.size });
        this.toastr.danger('Empty response', this.translate.instant('cv.errors.title') || 'Download failed');
        return;
      }
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = this.deriveDownloadName(resp);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (e) {
      console.error('cv-generate-panel: download failed', e, { url });
      this.toastr.danger(String(e), this.translate.instant('cv.errors.title') || 'Download failed');
    } finally {
      this.downloading = false;
    }
  }

  private deriveDownloadName(resp: Response): string {
    const cd = resp.headers.get('content-disposition') || '';
    const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
    if (m && m[1]) return decodeURIComponent(m[1].trim());
    return 'cv.pdf';
  }

  private handleStaleIds(_staleIds: unknown, prevPayload: CvCurationPayload): void {
    this.toastr.warning(
      this.translate.instant('cv.errors.staleSelections')
      || 'Some selections are out of date; refreshing...',
      this.translate.instant('cv.errors.title') || 'Notice'
    );

    const params: { client_id?: number } = {};
    if (prevPayload.client_id) {
      params.client_id = prevPayload.client_id;
    }
    const sub = this.cvService.getDefaults(this.userId, params).subscribe({
      next: fresh => {
        this.restoreValidSelections(fresh, prevPayload);
      },
      error: (err: unknown) => {
        console.error('Failed to refresh CV defaults after stale ids', err);
        this.toastr.danger(
          this.translate.instant('cv.errors.loadDefaultsFailed')
          || 'Could not load CV generator defaults.',
          this.translate.instant('cv.errors.title') || 'Error'
        );
      }
    });
    this.subs.push(sub);
  }

  private restoreValidSelections(fresh: CvDefaults, prev: CvCurationPayload): void {
    this.defaults = fresh;
    const freshDefaults = fresh.curation_defaults || ({} as CvCurationPayload);
    const intersection = (prevIds: number[] | undefined, freshIds: number[] | undefined): number[] => {
      if (!prevIds || !freshIds) return freshIds ?? [];
      const freshSet = new Set(freshIds);
      return prevIds.filter(id => freshSet.has(id));
    };

    const mergedCuration: CvCurationPayload = {
      client_id: prev.client_id,
      template_id: fresh.templates.some(t => t.id === prev.template_id)
        ? prev.template_id
        : (fresh.templates[0]?.id ?? prev.template_id),
      locale: prev.locale,
      vacancy_id: prev.vacancy_id ?? null,
      // Validate against the OPTION universe, not the default-selected set:
      // project rows are valid selections but default-unselected, so an
      // intersection with experience_ids would silently strip every project
      // opt-in on any stale-id refresh.
      experience_ids: intersection(
        prev.experience_ids,
        (freshDefaults.experience_options ?? []).length > 0
          ? (freshDefaults.experience_options ?? []).map(o => o.id)
          : freshDefaults.experience_ids
      ),
      education_ids: intersection(prev.education_ids, freshDefaults.education_ids),
      skill_ids: intersection(prev.skill_ids, freshDefaults.skill_ids),
      language_ids: intersection(prev.language_ids, freshDefaults.language_ids),
      references_text: prev.references_text,
      include_references: prev.include_references ?? null
    };

    // Rebuild the form with the merged curation set.
    this.buildForm({ ...fresh, curation_defaults: mergedCuration });
    this.triggerPreview();
  }

  // --------------------------------------------------------------------
  // Close
  // --------------------------------------------------------------------

  public onClose(): void {
    this.closed.emit();
  }

  // --------------------------------------------------------------------
  // Tailor mode
  // --------------------------------------------------------------------

  /** Currently-selected client id (form control, falling back to @Input). */
  public get currentClientId(): number {
    const fromForm = Number(this.form?.get('client_id')?.value);
    if (!Number.isNaN(fromForm) && fromForm > 0) return fromForm;
    return Number(this.clientId ?? 0);
  }

  /** True when the selected client is entitled to the AI Tailor mode. */
  public canTailor(): boolean {
    const id = this.currentClientId;
    return id > 0 && this.tailoringClientIds.includes(id);
  }

  /**
   * Flip to the routed tailor intake. Closes the host dialog FIRST so the route
   * does not render behind the modal (#503 class), then navigates carrying the
   * picked template/locale/branded selection so the panel path keeps its branded
   * selection (a branded client_cv_template_id travels with its client so the
   * backend ownership guard passes).
   */
  public flipToTailor(): void {
    const f = this.form?.value;
    this.closed.emit();
    void this.router.navigate(['/cv-generator/tailor/new'], {
      queryParams: {
        subject: this.userId,
        vacancy: this.vacancyId ?? null,
        client: this.currentClientId,
        template: f?.template_id,
        locale: f?.locale,
        template_type: f?.template_type,
        client_cv_template_id: f?.client_cv_template_id ?? null,
      },
    });
  }

  // --------------------------------------------------------------------
  // Template helpers for the HTML
  // --------------------------------------------------------------------

  public get hasDefaults(): boolean {
    return !!this.defaults && !!this.form;
  }

  public get templates(): CvTemplate[] {
    return this.defaults?.templates ?? [];
  }

  /**
   * Enriched checklist options for each curation section. Falls back to
   * synthesising a list from the raw *_ids array if the backend did not send
   * `*_options` yet (older server, stale cache). This keeps the UI sensible
   * even during rolling deploys where backend and frontend are out of sync.
   */
  public get experienceOptions(): CvCurationOption[] {
    return this.resolveOptions('experience_options', 'experience_ids');
  }

  public get educationOptions(): CvCurationOption[] {
    return this.resolveOptions('education_options', 'education_ids');
  }

  public get skillOptions(): CvCurationOption[] {
    return this.resolveOptions('skill_options', 'skill_ids');
  }

  public get languageOptions(): CvCurationOption[] {
    return this.resolveOptions('language_options', 'language_ids');
  }

  private resolveOptions(
    optionsKey: 'experience_options' | 'education_options' | 'skill_options' | 'language_options',
    idsKey: 'experience_ids' | 'education_ids' | 'skill_ids' | 'language_ids'
  ): CvCurationOption[] {
    const cur = this.defaults?.curation_defaults;
    if (!cur) return [];
    const opts = cur[optionsKey];
    if (Array.isArray(opts) && opts.length > 0) return opts;
    const ids = cur[idsKey];
    if (Array.isArray(ids) && ids.length > 0) {
      return ids.map(id => ({ id, label: `#${id}` }));
    }
    return [];
  }

  public clientLabel(id: number): string {
    const c = this.defaults?.clients?.find(x => x.id === id);
    if (!c) return '';
    return c.position ? `${c.name} - ${c.position}` : c.name;
  }

  /**
   * Map a locale code to a human-friendly display name. Unknown codes fall
   * through to their uppercase form so we never render an empty label.
   */
  public localeLabel(code: string): string {
    if (!code) return '';
    return this.localeLabels[code] ?? code.toUpperCase();
  }
}
